import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useRef, useState } from "react";
import { AgentSessionType } from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";
import { routes } from "schemas/routes";

import Markdown from "components/Markdown";
import TypingLoader from "features/Home/Sidebar/ChatHelper/TypingLoader";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";

import { Message } from "./schema";
import styles from "./styles";

interface Props {
  handleBack: () => void;
  handleNext: (updatedForm?: Map) => void;
  handleSkip?: () => void;
}

const ConversationalTemplate = ({
  handleBack,
  handleNext,
  handleSkip,
}: Props) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [agentSessionId, setAgentSessionId] = useState<number | null>(null);

  const [loadingMessage, setLoadingMessage] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);

  const [hasTemplate, setHasTemplate] = useState(false);

  const initializeTemplate = async () => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/init`,
        "POST",
        {},
        {
          type: AgentSessionType.template_onboarding,
        },
      );
      setAgentSessionId(res.session.id);
      if (res.session) {
        setMessages(res.session.items);
        if (res.session.items?.length === 1) {
          const lastMessage =
            res.session.items[res.session.items.length - 1]?.content;
          displayAssistantMessage(lastMessage);
        }
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUserInput = async (input: string) => {
    if (!input.trim()) return;
    setMessages((prev) => [...prev, { role: "user", content: input }]);

    setLoading(true);
    if (messages?.length === 1 && !loadingMessage) {
      if (input?.includes("do not")) {
        setLoadingMessage("Responding...");
      } else {
        setLoadingMessage("Analyzing your portfolio... 🔎");
      }
    }
    try {
      const { items } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${agentSessionId}/run`,
        "POST",
        {},
        {
          userInput: input,
          type: AgentSessionType.template_onboarding,
        },
      );
      setMessages(items);
      setInput("");
      const lastMessage = items[items.length - 1]?.content;
      displayAssistantMessage(lastMessage);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const handleCreateTemplate = async () => {
    setLoading(true);
    try {
      const { outreach, followup } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${agentSessionId}/template`,
        "POST",
      );
      handleStorePortfolio();
      handleNext({ outreach, followup });
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStorePortfolio = async () => {
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${agentSessionId}/store-portfolio`,
        "POST",
      );
    } catch (error) {
      // Failed silently if we can't store anything from the conversation
      // This is okay because we can just re-ask the user for the info again in the next convo.
    }
  };
  const displayAssistantMessage = (text: string) => {
    if (!text) return;
    setTyping(true);
    let index = 0;

    const interval = setInterval(() => {
      setMessages((prev) => {
        const updated = [...prev];
        const assistantMsg = { ...updated[updated.length - 1] };

        assistantMsg.content = text.slice(0, index + 1);
        updated[updated.length - 1] = assistantMsg;

        return updated;
      });

      index++;
      if (index >= text.length) {
        clearInterval(interval);
        setTyping(false);
      }
    }, 10);
  };

  useEffect(() => {
    if (messages && messages?.length < 1) {
      initializeTemplate();
    } else if (messages && messages?.length > 1) {
      const hasGeneratedTemplate = messages.some(
        (msg) => msg.role === "assistant" && msg.is_template,
      );
      setHasTemplate(hasGeneratedTemplate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages?.length]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, typing]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={isPage(routes.onboardingVaSelfServe) ? { p: 3 } : {}}
    >
      <Typography variant="h4" gutterBottom>
        Create A Template With Bento's AI
      </Typography>
      <Box flexGrow={1} overflow="auto" mb={2}>
        {messages.map((msg, i) => (
          <Box>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
              }}
              key={i}
            >
              <Paper
                elevation={0}
                sx={{
                  px: msg.role === "user" ? 2 : 0,
                  py: 0.75,
                  my: 1,
                  bgcolor: msg.role === "user" ? "primary.main" : "transparent",
                  whiteSpace: "pre-wrap",
                  borderRadius: "16px",
                }}
              >
                <Markdown
                  content={msg.content}
                  color={msg.role === "user" ? "white" : "inherit"}
                />
              </Paper>
            </Box>
          </Box>
        ))}

        {loading && (
          <Typography
            component="div"
            variant="body2"
            color="text.secondary"
            ml={1}
          >
            <Paper
              elevation={0}
              sx={{
                py: 0.75,
                my: 1,
                bgcolor: "transparent",
                whiteSpace: "pre-wrap",
              }}
            >
              <TypingLoader title={loadingMessage || "Responding..."} />
            </Paper>
          </Typography>
        )}

        <div ref={chatEndRef} />
      </Box>

      <Box
        component="form"
        onSubmit={(e: CustomEvent) => {
          e.preventDefault();
          handleSubmitUserInput(input);
        }}
        display="flex"
        alignItems={"center"}
        gap={1}
        sx={{
          pr: 3,
          border: "1px black solid",
          borderRadius: 7,
          overflow: "hidden",
        }}
      >
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          fullWidth
          placeholder={
            messages?.length === 1
              ? "Type your portfolio link here (e.g https://ugc.my.canva.site) or say 'I do not have it'"
              : "Type your message here..."
          }
          disabled={loading || typing}
          multiline
          minRows={1}
          maxRows={10}
          onKeyDown={(e: CustomEvent) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmitUserInput(e.target.value);
            }
          }}
          sx={styles.textField}
        />
        {loading || typing ? (
          <CircularProgress size={20} />
        ) : (
          <IconButton
            type="submit"
            disabled={loading || typing}
            sx={styles.submitButton}
          >
            <Box component="i" className="fa-regular fa-circle-arrow-up" />
          </IconButton>
        )}
      </Box>

      <DialogActions>
        <Grid container justifyContent={"center"} sx={{ mt: 3 }} gap={1}>
          <Grid item xs="auto">
            <Button
              onClick={handleBack}
              disableElevation
              sx={{ backgroundColor: "#F1F2EF", py: 1, px: 3 }}
            >
              <Box
                component="i"
                className="fa-regular fa-circle-arrow-left"
                sx={{ mr: 1 }}
              />{" "}
              Back
            </Button>
          </Grid>

          <Grid item xs="auto">
            <Button
              onClick={handleCreateTemplate}
              disableElevation
              disabled={loading || typing || !hasTemplate}
              variant="contained"
              sx={{ py: 1, px: 6 }}
            >
              Next{" "}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Box>
  );
};

export default ConversationalTemplate;
